import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { seesAllLeads } from '@functions/allowed-routes';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Chip, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import VideocamIcon from '@mui/icons-material/Videocam';
import GoogleIcon from '@mui/icons-material/Google';
import { authStorage } from 'functions/auth-session';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import type { DateSelectArg, DatesSetArg, EventClickArg, EventInput } from '@fullcalendar/core';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';
import esLocale from '@fullcalendar/core/locales/es';
import DashboardLayout from '@examples/LayoutContainers/DashboardLayout';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { StandaloneAutoCompleteSelect } from '@components/StandaloneAutoCompleteSelect';
import { getCrmColors, inkOver } from '../kanban-board/crm-theme';
import { useCrmUsers } from '../kanban-board/hooks/use-crm-users.hook';
import { CrmScheduleService } from '../kanban-board/services/crm-schedule.service';
import { AgendaItemResponse } from '../kanban-board/services/responses/lead-schedule.response';
import { useAvailableHeight } from '../hooks/use-available-height.hook';
import { AgendaStyles } from './agenda-styles';
import { AgendaMiniCalendar } from './components/agenda-mini-calendar';
import { AgendaNewActionModal } from './components/agenda-new-action-modal';
import { AgendaGoogleConnect } from './components/agenda-google-connect';
import { useGoogleCalendar } from './hooks/use-google-calendar.hook';
import { ExternalCalendarEvent, GoogleCalendarService } from './services/google-calendar.service';

/// Hora da ação criada por clique no mês, onde não há horário para clicar.
const DEFAULT_TIME = 9;

/// Compromissos por célula do mês antes de virar "+N": sem limite, um dia cheio estica a linha.
const MAX_PER_MONTH = 3;

export const Agenda = () => {
    const { t, i18n } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();
    const navigate = useNavigate();
    const { users } = useCrmUsers();
    const [searchParams] = useSearchParams();
    const google = useGoogleCalendar();

    /// O dia vem como AAAA-MM-DD; T00:00 evita o ISO puro virar o dia anterior em fuso negativo.
    const dayFromUrl = searchParams.get('dia');

    const [items, setItems] = useState<AgendaItemResponse[] | null>(null);
    const [external, setExternal] = useState<ExternalCalendarEvent[]>([]);
    const [period, setPeriod] = useState<{ from: string; to: string } | null>(null);
    const [assignee, setAssignee] = useState<string | null>(null);
    // Sem "vê todos os leads" o servidor só devolve as próprias tarefas: o filtro não teria efeito.
    const canFilterAssignee = useMemo(() => seesAllLeads(), []);
    const [visibleDay, setVisibleDay] = useState<Date | undefined>();
    const [miniAnchor, setMiniAnchor] = useState<HTMLElement | null>(null);
    const [newTime, setNewTime] = useState<string | null>(null);

    const calendarRef = useRef<FullCalendar | null>(null);

    // O FullCalendar avisa o intervalo a cada navegação; guardar evita recarregar se só o filtro muda.
    const range = useRef<{ from: string; to: string } | null>(null);

    /// Mede a página inteira da agenda, não só a grade.
    /// Piso baixo de propósito: a grade rola por dentro, e um piso alto empurraria a página.
    const { topRef, targetRef, height } = useAvailableHeight('', { bottomGap: 28, minHeight: 320 });

    const load = useCallback(
        async (from: string, to: string, userId: string | null) => {
            try {
                const res = await new CrmScheduleService().agenda(from, to, userId ?? undefined);
                setItems(res.data.data ?? []);
            } catch (err) {
                setItems([]);
                showAlert(apiErrorMessage(err), 'error', 4000);
            }
        },
        [showAlert]
    );

    const onPeriodChange = (arg: DatesSetArg) => {
        const from = arg.start.toISOString();
        const to = arg.end.toISOString();

        range.current = { from, to };
        setPeriod({ from, to });
        setVisibleDay(arg.start);
        load(from, to, assignee);
    };

    const reload = () => {
        if (!range.current) return;

        load(range.current.from, range.current.to, assignee);
    };

    const changeAssignee = (value: string | null) => {
        setAssignee(value);

        if (!range.current) return;

        load(range.current.from, range.current.to, value);
    };

    const goToDay = (day: Date) => {
        setMiniAnchor(null);
        calendarRef.current?.getApi().gotoDate(day);
    };

    /// O datetime-local espera hora local sem fuso; no mês não há horário, então usa hora comercial.
    const openNewAction = (arg: DateSelectArg) => {
        const when = new Date(arg.start);

        if (arg.allDay) when.setHours(DEFAULT_TIME, 0, 0, 0);

        const local = new Date(when.getTime() - when.getTimezoneOffset() * 60000);

        setNewTime(local.toISOString().slice(0, 16));
    };

    /// O Google do usuário só entra na agenda dele ou da equipe, não na filtrada por outro vendedor.
    const showGoogle = google.usable && (assignee === null || assignee === authStorage.read()?.userId);

    useEffect(() => {
        if (!showGoogle || !period) {
            setExternal([]);
            return;
        }

        new GoogleCalendarService()
            .events(period.from, period.to)
            .then((res) => setExternal(res.data.data ?? []))
            .catch(() => setExternal([]));
    }, [showGoogle, period]);

    const assigneeOptions = useMemo(
        () => users.map((user) => ({ value: user.id, label: user.name })),
        [users]
    );

    const events: EventInput[] = useMemo(
        () =>
            (items ?? [])
                // Concluído sai da grade: a tela mostra o que falta fazer.
                .filter((item) => !item.closedAt)
                .map((item): EventInput => {
                    const color = item.statusColor || cc.accent;

                    return {
                        id: item.id,
                        title: item.leadName,
                        start: item.scheduledAt,
                        // O agendamento não tem duração no modelo; meia hora é só o tamanho do bloco.
                        end: new Date(
                            new Date(item.scheduledAt).getTime() + 30 * 60 * 1000
                        ).toISOString(),
                        backgroundColor: color,
                        borderColor: color,
                        textColor: inkOver(color),
                        extendedProps: item,
                    };
                })
                // Os do Google entram apagados e sem cor de temperatura: só mostram horário ocupado.
                .concat(
                    external.map((event) => ({
                        id: `google-${event.id}`,
                        title: event.title,
                        start: event.allDay ? event.start.slice(0, 10) : event.start,
                        end: event.allDay ? event.end.slice(0, 10) : event.end,
                        allDay: event.allDay,
                        backgroundColor: cc.countBg,
                        borderColor: cc.border,
                        textColor: cc.textSecondary,
                        extendedProps: { external: true, title: event.title },
                    }))
                ),
        [items, external, cc.accent, cc.countBg, cc.border, cc.textSecondary]
    );

    const openLead = (arg: EventClickArg) => {
        if (arg.event.extendedProps.external) return;

        const item = arg.event.extendedProps as AgendaItemResponse;

        // O ícone do Meet fica no bloco e o FullCalendar trata o clique antes do React.
        if (item.meetUrl && (arg.jsEvent.target as HTMLElement).closest('[data-meet]')) {
            window.open(item.meetUrl, '_blank', 'noopener');
            return;
        }
        navigate(`/crm/lead/${item.leadId}`);
    };

    // Inglês é o padrão do FullCalendar: passar locale nenhum já é o certo.
    const locale = i18n.language?.startsWith('es')
        ? esLocale
        : i18n.language?.startsWith('en')
            ? undefined
            : ptBrLocale;

    const openCount = (items ?? []).filter((item) => !item.closedAt).length;
    const overdue = (items ?? []).filter(
        (item) => !item.closedAt && new Date(item.scheduledAt) < new Date()
    ).length;

    return (
        <DashboardLayout>

            {/* Altura travada: quem rola é a grade; cabeçalho e legenda não encolhem. */}
            <Box
                ref={targetRef}
                sx={{
                    background: cc.pageBg,
                    borderRadius: 3,
                    p: 2,
                    height: height ?? undefined,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <Box
                    ref={topRef}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                        mb: 2,
                        flexShrink: 0,
                    }}
                >
                    {/* Contadores na linha do título: centralizados, pairavam soltos entre título e descrição. */}
                    <Box sx={{ minWidth: 0 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Typography
                                sx={{ fontSize: 20, fontWeight: 700, color: cc.textPrimary }}
                            >
                                {t('agenda.titulo')}
                            </Typography>

                            <Chip
                                size="small"
                                label={t('agenda.abertos', { total: openCount })}
                                sx={{
                                    bgcolor: cc.countBg,
                                    color: cc.textPrimary,
                                    fontWeight: 700,
                                    fontSize: 11,
                                }}
                            />

                            {overdue > 0 && (
                                <Chip
                                    size="small"
                                    label={t('agenda.atrasados', { total: overdue })}
                                    sx={{
                                        bgcolor: `${cc.eventDanger}22`,
                                        color: cc.onTint,
                                        fontWeight: 700,
                                        fontSize: 11,
                                    }}
                                />
                            )}
                        </Box>

                        <Typography variant="caption" sx={{ color: cc.textSecondary }}>
                            {t('agenda.ajuda')}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto' }}>
                        <AgendaGoogleConnect status={google.status} onChanged={google.reload} />

                        {canFilterAssignee && (
                            <Box sx={{ width: 240 }}>
                                <StandaloneAutoCompleteSelect
                                    options={assigneeOptions}
                                    value={assignee}
                                    onChange={(value) => changeAssignee((value as string) ?? null)}
                                    placeholder={t('agenda.todosResponsaveis')}
                                    isClearable
                                />
                            </Box>
                        )}

                        {/* Mini-calendário em popover: uma coluna fixa espremeria a semana com o sidenav. */}
                        <Tooltip title={t('agenda.irParaData')}>
                            <IconButton
                                onClick={(e) => setMiniAnchor(e.currentTarget)}
                                sx={{
                                    width: 43,
                                    height: 43,
                                    bgcolor: cc.surfaceBg,
                                    border: `1px solid ${cc.border}`,
                                    borderRadius: '9px',
                                    color: cc.textPrimary,
                                    '&:hover': { bgcolor: cc.countBg, borderColor: cc.borderStrong },
                                }}
                            >
                                <CalendarMonthIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                <Popover
                    open={Boolean(miniAnchor)}
                    anchorEl={miniAnchor}
                    onClose={() => setMiniAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    slotProps={{ paper: { sx: { bgcolor: cc.cardBg, p: 1.5, borderRadius: 3 } } }}
                >
                    <AgendaMiniCalendar
                        cc={cc}
                        selected={visibleDay}
                        onChooseDay={goToDay}
                    />
                </Popover>

                <Box sx={{ flex: 1, minHeight: 0 }}>
                    <AgendaStyles cc={cc} fill>
                        <FullCalendar
                            ref={calendarRef}
                            plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
                            initialView={dayFromUrl ? "timeGridDay" : "timeGridWeek"}
                            initialDate={dayFromUrl ? new Date(`${dayFromUrl}T00:00:00`) : undefined}
                            locale={locale}
                            headerToolbar={{
                                left: 'prev,next today',
                                center: 'title',
                                right: 'timeGridDay,timeGridWeek,dayGridMonth',
                            }}
                            events={events}
                            datesSet={onPeriodChange}
                            eventClick={openLead}
                            selectable
                            selectMirror
                            select={openNewAction}
                            dayMaxEvents={MAX_PER_MONTH}
                            eventContent={(arg) => {
                                if (arg.event.extendedProps.external) {
                                    return (
                                        <Tooltip title={t('googleAgenda.naSuaAgenda')}>
                                            <Box
                                                sx={{
                                                    px: 0.7,
                                                    py: 0.3,
                                                    fontSize: 11,
                                                    lineHeight: 1.3,
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    cursor: 'default',
                                                }}
                                            >
                                                <GoogleIcon sx={{ fontSize: 11, verticalAlign: '-1px', mr: 0.4 }} />
                                                {arg.event.extendedProps.title}
                                            </Box>
                                        </Tooltip>
                                    );
                                }

                                const item = arg.event.extendedProps as AgendaItemResponse;

                                // No mês cada evento é uma tirinha de uma linha: duas linhas inchariam a célula.
                                const singleLine = arg.view.type === 'dayGridMonth';

                                return (
                                    <Tooltip
                                        title={`${item.leadName} — ${item.description}${
                                            item.userName ? ` · ${item.userName}` : ''
                                        }`}
                                    >
                                        <Box sx={{ px: 0.7, py: singleLine ? 0.15 : 0.45, overflow: 'hidden' }}>
                                            <Box
                                                sx={{
                                                    fontSize: singleLine ? 11 : 12,
                                                    fontWeight: 700,
                                                    lineHeight: 1.3,
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {item.meetUrl && (
                                                    <Tooltip title={t('googleAgenda.entrarNoMeet')}>
                                                        <VideocamIcon
                                                            data-meet
                                                            sx={{ fontSize: 13, verticalAlign: '-2px', mr: 0.4, cursor: 'pointer' }}
                                                        />
                                                    </Tooltip>
                                                )}
                                                {singleLine
                                                    ? `${arg.timeText} ${item.leadName}`
                                                    : item.leadName}
                                            </Box>

                                            {!singleLine && (
                                                <Box
                                                    sx={{
                                                        fontSize: 11,
                                                        opacity: 0.85,
                                                        lineHeight: 1.3,
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                >
                                                    {item.description}
                                                </Box>
                                            )}
                                        </Box>
                                    </Tooltip>
                                );
                            }}
                            // A ação do CRM sempre tem hora: dia inteiro só para feriado ou férias do Google.
                            allDaySlot={external.some((event) => event.allDay)}
                            // Dia inteiro na grade: quem marca às 21h30 ou 5h não via o compromisso.
                            slotMinTime="00:00:00"
                            slotMaxTime="24:00:00"
                            // Abre no começo do expediente: com o dia inteiro, o padrão seria a madrugada.
                            scrollTime="07:00:00"
                            nowIndicator
                            // Preenche o quadro, que já tem a altura do flex; pixels exigiriam descontar padding.
                            height="100%"
                            // Só estica a linha quando sobra altura; senão a grade rola, mantendo a escala.
                            expandRows
                        />
                    </AgendaStyles>
                </Box>

                <Typography
                    variant="caption"
                    sx={{ color: cc.textMuted, display: 'block', mt: 1.5, flexShrink: 0 }}
                >
                    {t('agenda.legenda')}
                </Typography>
            </Box>

            <AgendaNewActionModal
                open={newTime !== null}
                time={newTime ?? ''}
                googleReady={google.usable}
                onCancel={() => setNewTime(null)}
                onCreated={() => {
                    setNewTime(null);
                    showAlert(t('general.cadastro'), 'success', 2000);
                    reload();
                }}
            />
        </DashboardLayout>
    );
};
